import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that renders a paginated blog posts list with search and tag filters.
 */
export default class BlogPostsList extends PaginatedList {
  /** @type {HTMLFormElement | null} */
  form = null;

  /** @type {HTMLInputElement | null} */
  searchInput = null;

  /** @type {HTMLButtonElement | null} */
  clearButton = null;

  /** @type {HTMLParagraphElement | null} */
  emptyState = null;

  /** @type {HTMLButtonElement[]} */
  tagButtons = [];

  /** @type {string} */
  activeTag = '';

  /** @type {boolean} */
  allPagesLoaded = false;

  /** @type {number | null} */
  searchDebounce = null;

  /** @type {Promise<void> | null} */
  loadingAllPagesPromise = null;

  connectedCallback() {
    super.connectedCallback();

    const filterRoot = this.querySelector('[data-blog-filter]');
    if (!filterRoot) {
      return;
    }

    this.form = filterRoot.querySelector('[data-blog-filter-form]');
    this.searchInput = filterRoot.querySelector('[data-blog-filter-search]');
    this.clearButton = filterRoot.querySelector('[data-blog-filter-clear]');
    this.emptyState = this.querySelector('[data-blog-filter-empty]');
    this.tagButtons = Array.from(filterRoot.querySelectorAll('[data-blog-filter-tag]'));

    this.form?.addEventListener('submit', this.#handleSubmit);
    this.form?.addEventListener('reset', this.#handleReset);
    this.searchInput?.addEventListener('input', this.#handleInput);
    this.searchInput?.addEventListener('search', this.#handleSearch);
    this.clearButton?.addEventListener('click', this.#handleClearClick);
    this.tagButtons.forEach((button) => button.addEventListener('click', this.#handleTagClick));

    this.#syncFromURL();
    this.#updateClearButton();
  }

  disconnectedCallback() {
    if (this.searchDebounce) {
      window.clearTimeout(this.searchDebounce);
      this.searchDebounce = null;
    }

    this.form?.removeEventListener('submit', this.#handleSubmit);
    this.form?.removeEventListener('reset', this.#handleReset);
    this.searchInput?.removeEventListener('input', this.#handleInput);
    this.searchInput?.removeEventListener('search', this.#handleSearch);
    this.clearButton?.removeEventListener('click', this.#handleClearClick);
    this.tagButtons.forEach((button) => button.removeEventListener('click', this.#handleTagClick));

    super.disconnectedCallback();
  }

  #handleSubmit = (event) => {
    event.preventDefault();
    this.#applyFilters();
  };

  #handleReset = () => {
    this.activeTag = '';
    this.tagButtons.forEach((button, index) => {
      const isActive = index === 0;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
    this.#applyFilters();
    this.#updateClearButton();
  };

  #handleInput = () => {
    this.#updateClearButton();
    if (this.searchDebounce) {
      window.clearTimeout(this.searchDebounce);
    }

    this.searchDebounce = window.setTimeout(() => {
      this.#applyFilters();
    }, 250);
  };

  #handleSearch = () => {
    // Triggered when the built-in clear button is used on some browsers
    this.#applyFilters();
    this.#updateClearButton();
  };

  #handleClearClick = (event) => {
    event.preventDefault();
    this.form?.reset();
  };

  #handleTagClick = (event) => {
    event.preventDefault();

    const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const tag = button.dataset.blogFilterTag ?? '';

    this.activeTag = tag;
    this.tagButtons.forEach((btn) => {
      const isActive = btn === button;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    this.#applyFilters();
  };

  async #applyFilters() {
    await this.#ensureAllPagesLoaded();

    const grid = this.refs.grid;
    if (!grid) return;

    const searchTerm = (this.searchInput?.value ?? '').trim().toLowerCase();
    const activeTag = this.activeTag;

    let visibleCount = 0;

    grid.querySelectorAll('[ref="cards[]"]').forEach((card) => {
      if (!(card instanceof HTMLElement)) return;

      const searchText = card.dataset.searchText?.toLowerCase() ?? '';
      const tags = card.dataset.tags?.split('|') ?? [];

      const matchesTag = !activeTag || tags.includes(activeTag);
      const matchesSearch = !searchTerm || searchText.includes(searchTerm);

      const shouldShow = matchesTag && matchesSearch;
      card.hidden = !shouldShow;

      if (shouldShow) {
        visibleCount += 1;
      }
    });

    if (this.emptyState) {
      this.emptyState.hidden = visibleCount !== 0;
    }

    this.#updateURL();
  }

  async #ensureAllPagesLoaded() {
    if (this.allPagesLoaded) return;
    if (this.loadingAllPagesPromise) {
      await this.loadingAllPagesPromise;
      return;
    }

    const grid = this.refs.grid;
    if (!grid) return;

    const lastPage = Number(grid.dataset.lastPage || '1');
    if (!Number.isFinite(lastPage) || lastPage <= 1) {
      this.allPagesLoaded = true;
      return;
    }

    const baseUrl = new URL(window.location.href);
    baseUrl.hash = '';

    this.loadingAllPagesPromise = (async () => {
      for (let page = 2; page <= lastPage; page += 1) {
        const url = new URL(baseUrl);
        url.searchParams.set('page', page.toString());

        const html = await sectionRenderer.getSectionHTML(this.sectionId, true, url);
        const fragment = new DOMParser().parseFromString(html, 'text/html');
        const nextGrid = fragment.querySelector('[ref="grid"]');
        if (!nextGrid) continue;

        const cards = nextGrid.querySelectorAll('[ref="cards[]"]');
        cards.forEach((card) => {
          if (!(card instanceof HTMLElement)) return;
          card.dataset.page = '1';
          grid.append(card);
        });
      }

      grid.dataset.lastPage = '1';
      this.allPagesLoaded = true;
    })();

    try {
      await this.loadingAllPagesPromise;
    } finally {
      this.loadingAllPagesPromise = null;
    }
  }

  #updateClearButton() {
    if (!this.clearButton || !this.searchInput) return;
    this.clearButton.hidden = !(this.searchInput.value?.trim().length > 0);
  }

  #syncFromURL() {
    const params = new URLSearchParams(window.location.search);
    const searchQuery = params.get('q') ?? '';
    const tag = params.get('tag') ?? '';

    if (this.searchInput && searchQuery) {
      this.searchInput.value = searchQuery;
    }

    if (tag && this.tagButtons.length) {
      const matchingButton = this.tagButtons.find(
        (button) => (button.dataset.blogFilterTag ?? '') === tag
      );

      if (matchingButton) {
        this.activeTag = tag;
        this.tagButtons.forEach((button) => {
          const isActive = button === matchingButton;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
      }
    } else {
      // Ensure first button is active by default
      this.tagButtons.forEach((button, index) => {
        const isActive = index === 0;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    if (searchQuery || tag) {
      this.#applyFilters();
    }
  }

  #updateURL() {
    const params = new URLSearchParams();
    const searchValue = (this.searchInput?.value ?? '').trim();
    if (searchValue) {
      params.set('q', searchValue);
    }

    if (this.activeTag) {
      params.set('tag', this.activeTag);
    }

    const newUrl = `${window.location.pathname}${
      params.toString() ? `?${params.toString()}` : ''
    }${window.location.hash}`;

    window.history.replaceState({}, '', newUrl);
  }
}

if (!customElements.get('blog-posts-list')) {
  customElements.define('blog-posts-list', BlogPostsList);
}
